import React, { FC, useRef, useState } from "react";
import { FileUploadOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { parse } from "papaparse";
import { FormCsvDataDto } from "@green0meter/api-client";

import {
  StyledListItemIcon,
  StyledListItemText,
  StyledMenuItem,
} from "../../ActionsDropdown.styled";

import { ConfirmationModal } from "@components/common";
import { useImportFormCsvData } from "@api";
import { useUserStore } from "@store/user";
import { useMultistepFormStore } from "@components/common/MultistepForm/MultistepForm.store";

const CsvImport: FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const { t } = useTranslation();
  const { user } = useUserStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    formSettings: {
      formBasics: { id },
    },
  } = useMultistepFormStore();
  const { importFormCsvDataAsync, isPendingImportFormCsvData } =
    useImportFormCsvData(user);

  const handleOpenModal = (): void => {
    setModalOpen(true);
  };

  const handleCloseModal = (): void => {
    setModalOpen(false);
  };

  const confirmImport = (): void => {
    fileInputRef.current?.click();
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];

    if (!file) return;

    parse(file, {
      header: true,
      complete: async (results) => {
        const parsedData = results.data as Record<string, string>[];
        const csvData: FormCsvDataDto[] = [];

        // Detect locale columns dynamically (e.g., questionCZ, questionSK, groupDE)
        const headers = parsedData.length > 0 ? Object.keys(parsedData[0]) : [];
        const questionLocaleKey = headers.find(
          (h) =>
            h.startsWith("question") && h !== "questionEN" && h !== "questionNumber",
        );

        parsedData.forEach((row) => {
          const formData: FormCsvDataDto = {
            id: row["id"] || "",
            questionNumber: row["questionNumber"] || row["question number"] || "",
            questionEN: row["questionEN"] || row["questionEN"] || "",
            questionLocale:
              (questionLocaleKey ? row[questionLocaleKey] : null) ||
              row["questionEN"] ||
              "",
            type: row["type"] || "",
            value: row["value"] || "",
            answerEn: row["answerEn"] || "",
            answerLocale: row["answerLocale"] || "",
            units: row["units"] || "",
            notes: row["notes"] || "",
            isVisible: row["isVisible"] !== "false",
            relevantYear: row["relevantYear"]
              ? parseInt(row["relevantYear"])
              : undefined,
            parentQuestionCode: row["parentQuestionCode"] || undefined,
            rowIndex: row["rowIndex"] ? parseInt(row["rowIndex"]) : undefined,
          };

          csvData.push(formData);
        });

        await importFormCsvDataAsync({
          formId: id,
          csvData,
        });

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        setModalOpen(false);
      },
    });
  };

  return (
    <>
      <StyledMenuItem onClick={handleOpenModal}>
        <StyledListItemIcon>
          <FileUploadOutlined fontSize="medium" />
        </StyledListItemIcon>
        <StyledListItemText>{t("forms.importCSV")}</StyledListItemText>
      </StyledMenuItem>
      <input
        type="file"
        accept=".csv"
        ref={fileInputRef}
        hidden
        onChange={handleImport}
      />
      <ConfirmationModal
        open={modalOpen}
        onClose={handleCloseModal}
        onConfirm={confirmImport}
        title={
          <>
            <FileUploadOutlined fontSize="large" />
            <br />
            {t("forms.importCSVTitle")}
          </>
        }
        isLoading={isPendingImportFormCsvData}
        noCancel={isPendingImportFormCsvData}
        description={t("forms.importCSVDescription")}
        confirmText={t("forms.importCSV")}
      />
    </>
  );
};

export default CsvImport;
